import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, UtensilsCrossed, Clock, Wrench, ThermometerSun, Refrigerator, Flame, Fan } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const RestaurantEquipmentRepairPage = () => {
  const navigate = useNavigate();

  const equipmentTypes = [
    {
      icon: Flame,
      title: "Ovens & Ranges",
      description: "Gas and electric oven repair, range calibration, burner replacement, igniter repair, and temperature control diagnostics for all commercial brands."
    },
    {
      icon: Refrigerator,
      title: "Refrigeration Units",
      description: "Walk-in coolers, reach-in refrigerators, freezers, and prep tables. We handle compressor issues, thermostat failures, refrigerant leaks, and more."
    },
    {
      icon: ThermometerSun,
      title: "Fryers & Grills",
      description: "Deep fryer repair, flat-top grill servicing, charbroiler maintenance, and thermostat recalibration to keep your cooking equipment running safely."
    },
    {
      icon: Fan,
      title: "Ventilation & Hoods",
      description: "Exhaust hood repair, ventilation system maintenance, fan motor replacement, and ductwork cleaning to maintain air quality and code compliance."
    },
    {
      icon: Wrench,
      title: "Dishwashers & Steam Equipment",
      description: "Commercial dishwasher repair, steam table servicing, booster heater maintenance, and water temperature issues for health code compliance."
    },
    {
      icon: Clock,
      title: "Preventive Maintenance",
      description: "Scheduled maintenance programs to keep all your commercial kitchen equipment running efficiently, prevent unexpected breakdowns, and extend equipment lifespan."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="restaurant-equipment-page">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button data-testid="call-btn" className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="w-20 h-20 rounded-full bg-[#DA9C3A] flex items-center justify-center mx-auto mb-6">
            <UtensilsCrossed size={40} />
          </div>
          <h1 className="romano-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="page-heading">
            Restaurant Equipment Repair
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto" data-speakable="true">
            Fast, reliable repair and maintenance for all your commercial kitchen essentials, from ovens and ranges to refrigerator units. We understand that downtime means lost revenue, so our certified technicians work quickly to get your kitchen back to peak efficiency.
          </p>
        </div>
      </div>

      {/* NAP Section */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center items-center gap-6 text-center sm:text-left">
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#622B62]">Romano Heating & Cooling</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-gray-300" />
            <div className="text-gray-600">1316 Church Street, Roseville, CA 95678</div>
            <div className="hidden sm:block w-px h-6 bg-gray-300" />
            <a href="tel:9162749021" className="text-[#DA9C3A] font-semibold hover:text-[#C48A2A]">
              (916) 274-9021
            </a>
          </div>
        </div>
      </div>

      {/* Equipment Types Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="romano-heading text-3xl font-bold text-[#622B62] mb-4">
            Equipment We Service
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our certified technicians are trained to repair and maintain all types of commercial kitchen equipment, keeping your restaurant running without interruption.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {equipmentTypes.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                data-testid={`equipment-card-${index}`}
                className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#622B62] flex items-center justify-center mb-5 group-hover:bg-[#DA9C3A] transition-colors">
                  <Icon size={26} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#622B62] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative z-10 text-center">
              <h2 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
                Why Choose Romano for Restaurant Equipment Repair?
              </h2>
              <div className="grid sm:grid-cols-2 gap-6 mt-8 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock size={16} />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Fast Response Time</h4>
                    <p className="text-white/80 text-sm">We know every hour of downtime costs you money. Our team responds quickly to minimize disruption to your business.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Wrench size={16} />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Certified Technicians</h4>
                    <p className="text-white/80 text-sm">Our technicians are factory-trained and certified to work on all major commercial kitchen equipment brands.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <UtensilsCrossed size={16} />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">All Equipment Types</h4>
                    <p className="text-white/80 text-sm">From ovens and fryers to refrigeration and ventilation, we handle repairs for your entire commercial kitchen.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Phone size={16} />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Upfront Pricing</h4>
                    <p className="text-white/80 text-sm">No surprises. We provide clear estimates before starting any work so you know exactly what to expect.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <a href="tel:9162749021">
                  <Button data-testid="cta-call-btn" className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                    <Phone size={22} className="mr-2" />
                    Call (916) 274-9021
                  </Button>
                </a>
                <Button
                  variant="outline"
                  data-testid="cta-contact-btn"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                  onClick={() => navigate('/#contact')}
                >
                  Request a Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={companyInfo.logo}
              alt="Romano Heating & Cooling"
              className="h-24 w-24 object-contain rounded-lg"
            />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">
            &copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RestaurantEquipmentRepairPage;
